import { useRef } from "react";
import { useRouter } from "next/router";
import {
  isBuilderView,
  isLibraryItemBuilderView,
} from "@src/features/app/top-nav/builderNav";
import { useActiveLibraryItemView } from "@src/features/libraryItem/contexts/libraryItemMainTabStore";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";

/**
 * Whether the current view is a content building surface, which the secondary
 * nav collapses out of the way for.
 *
 * On the redesigned library item detail page only the full-screen editor —
 * the "build" tab, entered through Edit — earns the collapse; every viewing
 * tab, the read-only Content view included, keeps the app nav. The previous
 * detail page, which the other item types still ship, is a builder on every
 * tab and collapses throughout, as it always has. Which page it is, and the
 * tab, are unknown until the item loads (the landing tab is derived from the
 * item), and an unknown page leaves the nav where it was: collapsing on a
 * guess and undoing it a moment later reads as a glitch, and so does opening
 * it for the round-trip between one item and the next.
 *
 * Without Ff.LibraryItemOverviewTab every detail page is the previous one,
 * and the answer is the route alone, as it always was.
 */
export const useIsAdminContentBuilderView = (): boolean => {
  const { pathname, query } = useRouter();
  const libraryItemView = useActiveLibraryItemView();
  const overviewTabEnabled = useFeatureFlag(Ff.LibraryItemOverviewTab);
  // The store empties whenever the detail page swaps one item for the next
  // (its provider unmounts until the new item arrives), so the last answer is
  // kept through that gap rather than re-derived from nothing.
  const lastDetailPageAnswer = useRef(false);
  // Whenever a redesigned page reports in, its tab is the answer.
  if (libraryItemView?.page === "redesigned") {
    lastDetailPageAnswer.current = libraryItemView.mainTab === "build";
    return lastDetailPageAnswer.current;
  }
  if (overviewTabEnabled && isLibraryItemBuilderView(pathname, query)) {
    // A redesigned page answered above, so a report here is the previous
    // page — a builder on every tab.
    if (libraryItemView != null) {
      lastDetailPageAnswer.current = libraryItemView.page === "previous";
    }
    return lastDetailPageAnswer.current;
  }
  lastDetailPageAnswer.current = false;
  return isBuilderView(pathname, query);
};
